import { getAuth, getRedirectResult, onIdTokenChanged } from 'firebase/auth'
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore'

/**
 * Singleton promise that resolves when Firebase Auth is fully ready
 * with a valid ID token for Firestore operations.
 *
 * Also handles the result from Google redirect sign-in if the user
 * was redirected back from Google.
 *
 * NOTE: Due to Firebase SDK internals, an initial "permission-denied" error
 * may still appear in the console during page load. This is benign - the SDK
 * automatically retries and succeeds. The error cannot be prevented without
 * significant delays that would hurt UX.
 */
let authReadyPromise: Promise<void> | null = null
let isAuthReady = false
let redirectUser: ReturnType<typeof getAuth>['currentUser'] | null = null

/**
 * If we just completed a redirect sign-in, return the user we got from the redirect.
 * This avoids race conditions with vuefire's getCurrentUser which may not be aware
 * of the redirect result yet.
 */
export function getRedirectUserIfPresent(): ReturnType<typeof getAuth>['currentUser'] {
  return redirectUser
}

/**
 * Handle Google redirect result if user came from a redirect flow
 * This must be called early before other auth operations
 * Returns the user if one was signed in via redirect
 */
async function handleRedirectIfNeeded(auth: ReturnType<typeof getAuth>): Promise<ReturnType<typeof getRedirectResult>> {
  try {
    const result = await getRedirectResult(auth)

    if (result?.user) {
      // Create session for the newly signed-in user
      try {
        const idToken = await result.user.getIdToken(true)
        await $fetch('/api/auth/create-session', {
          method: 'POST',
          body: {
            idToken,
            expiresIn: 60 * 60 * 24 * 14 * 1000, // 14 days in milliseconds
          },
        })
      }
      catch (sessionError) {
        console.error('[Auth] Failed to create session after redirect:', sessionError)
      }

      // Ensure member document exists
      try {
        const db = getFirestore()
        const docRef = doc(db, 'members', result.user.uid)
        const docSnap = await getDoc(docRef)

        if (!docSnap.exists()) {
          await setDoc(docRef, {
            email: result.user.email,
            created_at: new Date(),
            updated_at: new Date(),
            member_type: 'pending',
            name: result.user.displayName || '',
          })
        }
      }
      catch (memberError) {
        console.error('[Auth] Failed to create member doc after redirect:', memberError)
      }

      redirectUser = result.user

      // Don't navigate here - let the middleware handle it after auth is ready
      // This ensures the auth flow completes properly on all browsers
      return result
    }
    return null
  }
  catch (error) {
    // Log but don't throw - user may not have come from a redirect
    console.error('[Auth] Redirect result error:', error)
    return null
  }
}

/**
 * Returns a promise that resolves when Firebase Auth token is available.
 */
export function useAuthReady(): Promise<void> {
  if (isAuthReady) {
    return Promise.resolve()
  }

  if (authReadyPromise) {
    return authReadyPromise
  }

  authReadyPromise = new Promise((resolve) => {
    const auth = getAuth()

    // Always check for redirect result first, even if user appears to be signed in
    // This handles the case where Firebase restored auth state from cache
    // but we still need to process the redirect result for proper navigation
    handleRedirectIfNeeded(auth).then((redirectResult) => {
      // If we got a redirect result, auth is ready
      if (redirectResult?.user) {
        isAuthReady = true
        resolve()
        return
      }

      // No redirect result - check current auth state
      if (auth.currentUser) {
        // User is already signed in - just ensure we have a valid token
        auth.currentUser.getIdToken(false).then(() => {
          isAuthReady = true
          resolve()
        }).catch(() => {
          // Token might be expired, try force refresh
          auth.currentUser?.getIdToken(true).then(() => {
            isAuthReady = true
            resolve()
          }).catch(() => {
            isAuthReady = true
            resolve()
          })
        })
        return
      }

      // No current user - wait for auth state to load
      const unsubscribe = onIdTokenChanged(auth, async (user) => {
        if (user) {
          // Ensure token is obtained (don't force refresh on initial load)
          await user.getIdToken(false)
          isAuthReady = true
          unsubscribe()
          resolve()
        }
        else {
          isAuthReady = true
          unsubscribe()
          resolve()
        }
      })
    })
  })

  return authReadyPromise
}

/**
 * Reset the auth ready state (call on logout)
 */
export function resetAuthReady(): void {
  isAuthReady = false
  authReadyPromise = null
  redirectUser = null
}
